import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { ProviderOutcome, SessionAttempt, SessionContext, SessionCoordinator } from '@common/session/session-coordinator';
import type { ISignedSessionEnvelope } from '@common/types/api/signed-session';
import { IPreferredMicrosoftAccount, OfficeBase } from '@core/base/office-base';
import { SessionBase } from '@core/base/session-base';
import { ISessionInfo, ISessionTokenClaims } from '@core/base/session-base.types';
import { OfficeSubscription } from "@core/office/office-subscription/office-subscription";
import { environment } from '@env';
import { CHAT_COMPOSER_DRAFT_STORAGE_KEY, GROUP_CHAT_COMPOSER_DRAFT_STORAGE_KEY } from '@services/chat-composer/chat-composer.constants';
import { logger } from "@services/logger/logger.service";
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

interface OfficeStorageFence {
    readonly epoch: string | null;
    readonly sourceToken: string | null;
    readonly recovery: boolean;
    readonly legacyPair?: { readonly sessionId: string; readonly deviceHash: string };
}

interface OfficeSessionContext extends SessionContext
{
    readonly session: Readonly<ISessionInfo>;
    readonly sessionToken: string;
    readonly displayName: string | null;
    readonly storageFence: OfficeStorageFence | null;
    readonly clearLogoutFlag?: boolean;
    readonly preferredAccount?: IPreferredMicrosoftAccount;
}

// Preserve the public login contract: MSAL/login failures reject, a failed
// subsequent session check leaves the client unauthenticated without throwing.
class OfficeLoginFailure
{
    constructor(readonly cause: unknown) { }
}

const SESSION_ENDPOINTS = {
    CHECK_SESSION: `${ environment.office_api_host }/api/auth/session`,
    EXCHANGE: `${ environment.office_api_host }/api/auth/session/exchange`,
    ROTATE: `${ environment.office_api_host }/api/auth/session/rotate`,
    LOGOUT: `${ environment.office_api_host }/api/auth/logout`,
} as const;
const SIGNED_IN_ONCE_KEY = 'r2_office_signed_in_once';
const SIGNED_SESSION_KEY = 'r2_office_signed_session';
const STORAGE_EPOCH_KEY = 'r2_office_session_epoch';

interface PersistedOfficeSession {
    readonly version: 1;
    readonly apiHost: string;
    readonly sessionToken: string;
    readonly expiresAt: number;
    readonly account: { readonly subject: string; readonly tenantId: string;
        readonly email: string | null; readonly displayName: string | null };
}

export class OfficeSessionService extends SessionBase implements SessionBase
{
    private accessToken: string | null = null;
    private tokenClaims: ISessionTokenClaims | null = null;
    private readonly coordinator: SessionCoordinator<OfficeSessionContext>;
    private loginHint?: string;
    private logoutWork: Promise<void> | null = null;
    private needsAccountPicker = false;
    private rotatePersistedIds = false;
    private refreshTimer?: ReturnType<typeof setTimeout>;
    private readonly hadLegacyPair: boolean;
    private storageClearFence: OfficeStorageFence | null = null;
    private intentEpoch: string | null = null;
    private restoreSourceToken: string | null = null;
    private readonly clientSessionId = uuidv4();

    constructor(
        private http: HttpClient,
        private subscription: OfficeSubscription,
        private office: OfficeBase,
    )
    {
        const hadLegacyPair = Boolean(localStorage.getItem('session_id') && localStorage.getItem('device_hash'));
        super();
        this.hadLegacyPair = hadLegacyPair;
        this.coordinator = new SessionCoordinator<OfficeSessionContext>({
            clock: { now: () => Date.now() },
            provider: {
                recover: (previous, attempt) => this.recoverOffice(previous, attempt),
                login: attempt => this.loginWithOffice(attempt, this.loginHint),
                logout: previous => this.logoutOffice(previous?.sessionToken ?? null),
            },
            persistence: {
                read: async () =>
                {
                    await this.waitInit();
                    const raw = localStorage.getItem(SIGNED_SESSION_KEY);
                    this.restoreSourceToken = this.storedSessionToken(raw);
                    return this.persistedContext(raw);
                },
                write: context =>
                {
                    if (!context.storageFence) throw new Error('Office session storage fence is missing');
                    this.assertStorageFence(context.storageFence, context);
                    const stored: PersistedOfficeSession = {
                        version: 1, apiHost: environment.office_api_host,
                        sessionToken: context.sessionToken, expiresAt: context.expiresAt!,
                        account: { subject: context.session.user_id!, tenantId: context.session.tenant_id!,
                            email: context.session.user_email ?? null,
                            displayName: context.displayName },
                    };
                    localStorage.setItem(SIGNED_SESSION_KEY, JSON.stringify(stored));
                    this.restoreSourceToken = context.sessionToken;
                    localStorage.setItem(SIGNED_IN_ONCE_KEY, 'true');
                    // A recovery of an already signed bearer does not prove that a
                    // raw pair appearing in another pane belongs to this account.
                    const retireLegacy = !context.storageFence.recovery || !!context.storageFence.legacyPair
                        || (!localStorage.getItem('session_id') && !localStorage.getItem('device_hash'));
                    if (retireLegacy)
                    {
                        localStorage.removeItem('session_id');
                        localStorage.removeItem('device_hash');
                        localStorage.removeItem('r2_msal_preferred_account');
                        localStorage.removeItem('r2_msal_logged_out');
                    }
                },
                clear: async () =>
                {
                    const fence = this.storageClearFence;
                    if (fence && (this.storageEpoch() !== fence.epoch
                        || this.storedSessionToken() !== fence.sourceToken))
                    {
                        this.rotatePersistedIds = false;
                        return;
                    }
                    localStorage.removeItem(SIGNED_SESSION_KEY);
                    this.restoreSourceToken = null;
                    if (!this.rotatePersistedIds) return;
                    this.rotatePersistedIds = false;
                    await this.waitInit();
                    localStorage.removeItem('session_id');
                    localStorage.removeItem('device_hash');
                    await this.reinit();
                },
            },
            credentials: { set: context => this.publishContext(context) },
            transport: {
                reset: () =>
                {
                    this.authState$.next(false);
                    // Existing subscription reset also invalidates crypto and
                    // fences outstanding hydration. Office settings are retained.
                    this.subscription.reset();
                },
                // Office's transport adapter sets up keys lazily after the
                // authenticated notification. Never duplicate that exchange here.
                prepare: () => () => undefined,
            },
        });
    }

    public async check(): Promise<boolean>
    {
        const generation = this.coordinator.generation;
        await this.waitInit();
        const ready = this.coordinator.readiness();
        if (ready.generation === generation && ready.status === 'ready' && this.authState$.getValue() === true
            && this.contextMatchesStorage(ready.context)) return true;
        const outcome = await this.coordinator.recover(generation);
        if (outcome.status === 'unavailable') logger.error(this.constructor.name, 'Session check unavailable');
        return outcome.status === 'ready' && outcome.generation === this.coordinator.generation
            && this.coordinator.readiness().status === 'ready' && this.authState$.getValue() === true;
    }

    public getAccessToken(): string | null
    {
        const ready = this.coordinator.readiness();
        if (ready.status === 'ready' && !this.contextMatchesStorage(ready.context)) return null;
        return this.accessToken;
    }

    public getTokenClaims(): ISessionTokenClaims | null
    {
        const ready = this.coordinator.readiness();
        if (ready.status === 'ready' && !this.contextMatchesStorage(ready.context)) return null;
        return this.tokenClaims;
    }

    public async login(userHint?: string): Promise<void>
    {
        const sourceToken = this.storedSessionToken();
        this.intentEpoch = this.advanceStorageEpoch();
        this.storageClearFence = { epoch: this.intentEpoch, sourceToken, recovery: false };
        this.loginHint = userHint;
        const outcome = await this.coordinator.login();
        if (outcome.status === 'unavailable' && outcome.failure.error instanceof OfficeLoginFailure)
            throw outcome.failure.error.cause;
        if (outcome.status === 'ready' && outcome.generation === this.coordinator.generation
            && localStorage.getItem(SIGNED_IN_ONCE_KEY) !== 'true')
            localStorage.setItem(SIGNED_IN_ONCE_KEY, 'true');
    }

    private async loginWithOffice(attempt: SessionAttempt, userHint?: string): Promise<ProviderOutcome<OfficeSessionContext>>
    {
        await this.logoutWork;
        await this.waitInit();
        this.requireCurrent(attempt);
        const fence: OfficeStorageFence = { epoch: this.intentEpoch, sourceToken: null, recovery: false };
        let useAccountPicker = this.needsAccountPicker || this.office.isLoggedOutFlagSet();
        let preferred: IPreferredMicrosoftAccount | null = null;
        if (!useAccountPicker)
        {
            try
            {
                preferred = this.office.getPreferredAccount();
            }
            catch
            {
                useAccountPicker = true;
            }
        }
        try
        {
            let idToken: string;
            let usedSilentProof = false;
            if (useAccountPicker)
            {
                idToken = await this.office.acquireIdTokenInteractive(true);
            }
            else
            {
                let silent: string | null = null;
                try { silent = await this.office.acquireIdTokenSilent(userHint); }
                catch { /* A rejected silent attempt also needs interactive sign-in. */ }
                this.requireCurrent(attempt);
                usedSilentProof = silent !== null;
                idToken = silent ?? await this.office.acquireIdTokenInteractive(false, userHint);
            }
            this.requireCurrent(attempt);
            this.loginExchangeStartedSubject.next();
            let result: OfficeSessionContext;
            try
            {
                result = await this.exchange(idToken, attempt, usedSilentProof ? preferred : null, fence);
            }
            catch (error)
            {
                if (!usedSilentProof || !(error instanceof HttpErrorResponse) || error.status !== 401)
                {
                    throw error;
                }
                this.requireCurrent(attempt);
                idToken = await this.office.acquireIdTokenInteractive(false, userHint);
                this.requireCurrent(attempt);
                result = await this.exchange(idToken, attempt, null, fence);
            }
            this.requireCurrent(attempt);
            return { status: 'ready', context: {
                ...result,
                clearLogoutFlag: useAccountPicker,
                preferredAccount: {
                    tenantId: result.session.tenant_id!, subject: result.session.user_id!, loginHint: result.session.user_hint,
                },
            } };
        }
        catch (cause) { return { status: 'unavailable', error: new OfficeLoginFailure(cause) }; }
    }

    private async recoverOffice(previous: OfficeSessionContext | null, attempt: SessionAttempt): Promise<ProviderOutcome<OfficeSessionContext>>
    {
        await this.waitInit();
        this.requireCurrent(attempt);
        const epoch = this.storageEpoch();
        let fence: OfficeStorageFence = { epoch,
            sourceToken: previous?.sessionToken ?? this.restoreSourceToken, recovery: true };
        this.storageClearFence = fence;
        if (this.needsAccountPicker || this.office.isLoggedOutFlagSet()) return { status: 'invalid' };
        const preferred = this.office.getPreferredAccount();
        const latest = this.persistedContext(localStorage.getItem(SIGNED_SESSION_KEY));
        if (latest && ((previous && latest.identity !== previous.identity)
            || (preferred && (latest.session.tenant_id !== preferred.tenantId
                || latest.session.user_id !== preferred.subject)))) return { status: 'invalid' };
        if (latest && latest.sessionToken !== previous?.sessionToken)
        {
            previous = latest;
            fence = { epoch, sourceToken: latest.sessionToken, recovery: true };
            this.storageClearFence = fence;
        }
        if (previous && preferred && (previous.session.tenant_id !== preferred.tenantId
            || previous.session.user_id !== preferred.subject)) return { status: 'invalid' };
        if (previous)
        {
            if (previous.expiresAt !== null && Date.now() < previous.expiresAt)
            {
                try
                {
                    const valid = await firstValueFrom(this.http.get<boolean>(SESSION_ENDPOINTS.CHECK_SESSION,
                        { headers: { Authorization: `Bearer ${ previous.sessionToken }` } }));
                    this.requireCurrent(attempt);
                    if (valid === true) return { status: 'ready', context: { ...previous, storageFence: fence } };
                }
                catch (error)
                {
                    if (!(error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)))
                        return { status: 'unavailable', error };
                }
            }
            try
            {
                return { status: 'ready', context: await this.rotate(previous, attempt, preferred, fence) };
            }
            catch (error)
            {
                if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403))
                    return { status: 'invalid' };
                return { status: 'unavailable', error };
            }
        }
        if (this.hadLegacyPair && localStorage.getItem('session_id') && localStorage.getItem('device_hash')
            && (localStorage.getItem(SIGNED_IN_ONCE_KEY) !== 'true' || preferred))
        {
            try
            {
                return { status: 'ready', context: await this.exchangeLegacy(attempt, preferred, fence) };
            }
            catch (error)
            {
                if (preferred && error instanceof HttpErrorResponse && error.status === 401)
                {
                    this.requireCurrent(attempt);
                    const currentPreferred = this.office.getPreferredAccount();
                    if (this.office.isLoggedOutFlagSet() || this.storageEpoch() !== fence.epoch
                        || this.storedSessionToken() !== fence.sourceToken
                        || currentPreferred?.tenantId !== preferred.tenantId
                        || currentPreferred.subject !== preferred.subject)
                    {
                        return { status: 'invalid' };
                    }
                }
                else
                {
                    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403))
                    {
                        return { status: 'invalid' };
                    }
                    return { status: 'unavailable', error };
                }
            }
        }
        if (!preferred && localStorage.getItem(SIGNED_IN_ONCE_KEY) !== 'true') return { status: 'invalid' };
        try
        {
            this.requireCurrent(attempt);
            const idToken = await this.office.acquireIdTokenSilent(this.loginHint);
            this.requireCurrent(attempt);
            if (!idToken) return { status: 'invalid' };
            return { status: 'ready', context: await this.exchange(idToken, attempt, preferred, fence) };
        }
        catch (error) { return { status: 'unavailable', error }; }
    }

    private async exchange(idToken: string, attempt: SessionAttempt, preferred: IPreferredMicrosoftAccount | null,
        fence: OfficeStorageFence): Promise<OfficeSessionContext>
    {
        const envelope = await firstValueFrom(this.http.post<ISignedSessionEnvelope>(SESSION_ENDPOINTS.EXCHANGE,
            { provider: 'microsoft', idToken }));
        return this.contextFromEnvelope(envelope, preferred, false, attempt, fence);
    }

    private async exchangeLegacy(attempt: SessionAttempt, preferred: IPreferredMicrosoftAccount | null,
        fence: OfficeStorageFence): Promise<OfficeSessionContext>
    {
        const sessionId = localStorage.getItem('session_id');
        const deviceHash = localStorage.getItem('device_hash');
        if (!sessionId || !deviceHash) throw new Error('Legacy Office session is no longer available');
        const envelope = await firstValueFrom(this.http.post<ISignedSessionEnvelope>(SESSION_ENDPOINTS.EXCHANGE,
            { provider: 'microsoft', session_id: sessionId, device_hash: deviceHash,
                ...(preferred ? { expectedAccount: { subject: preferred.subject, tenantId: preferred.tenantId } } : {}) }));
        return this.contextFromEnvelope(envelope, preferred, false, attempt,
            { ...fence, legacyPair: { sessionId, deviceHash } });
    }

    private async rotate(previous: OfficeSessionContext, attempt: SessionAttempt,
        preferred: IPreferredMicrosoftAccount | null, fence: OfficeStorageFence): Promise<OfficeSessionContext>
    {
        const envelope = await firstValueFrom(this.http.post<ISignedSessionEnvelope>(SESSION_ENDPOINTS.ROTATE, {},
            { headers: { Authorization: `Bearer ${ previous.sessionToken }` } }));
        return this.contextFromEnvelope(envelope, preferred ?? {
            tenantId: previous.session.tenant_id!, subject: previous.session.user_id!,
        }, false, attempt, fence);
    }

    private contextFromEnvelope(envelope: ISignedSessionEnvelope, preferred: IPreferredMicrosoftAccount | null,
        allowExpired: boolean, attempt?: SessionAttempt, storageFence: OfficeStorageFence | null = null): OfficeSessionContext
    {
        if (attempt) this.requireCurrent(attempt);
        if (!envelope || typeof envelope.sessionToken !== 'string' || !envelope.sessionToken
            || !Number.isSafeInteger(envelope.expiresAt) || (!allowExpired && envelope.expiresAt <= Date.now())
            || envelope.account?.provider !== 'microsoft' || !envelope.account.subject || !envelope.account.tenantId)
            throw new Error('Invalid signed Microsoft session response');
        if (preferred && (envelope.account.tenantId !== preferred.tenantId || envelope.account.subject !== preferred.subject))
            throw new Error('Microsoft account does not match the selected account');
        const session: ISessionInfo = {
            session_id: this.clientSessionId,
            user_id: envelope.account.subject,
            tenant_id: envelope.account.tenantId,
            user_email: envelope.account.email ?? undefined,
            user_hint: envelope.account.email ?? undefined,
        };
        return Object.freeze({
            identity: `${ envelope.account.tenantId }:${ envelope.account.subject }`,
            contextKey: JSON.stringify([ environment.office_api_host, envelope.sessionToken ]),
            expiresAt: envelope.expiresAt,
            session: Object.freeze(session), sessionToken: envelope.sessionToken,
            displayName: envelope.account.displayName ?? null,
            storageFence,
        });
    }

    private storedSessionToken(raw: string | null = localStorage.getItem(SIGNED_SESSION_KEY)): string | null
    {
        if (!raw) return null;
        try
        {
            const value = JSON.parse(raw);
            return typeof value?.sessionToken === 'string' ? value.sessionToken : null;
        }
        catch { return null; }
    }

    private persistedContext(raw: string | null): OfficeSessionContext | null
    {
        if (!raw) return null;
        let stored: PersistedOfficeSession;
        try { stored = JSON.parse(raw) as PersistedOfficeSession; }
        catch { return null; }
        if (stored?.version !== 1 || stored.apiHost !== environment.office_api_host
            || typeof stored.sessionToken !== 'string' || !stored.sessionToken
            || !Number.isSafeInteger(stored.expiresAt)
            || typeof stored.account?.subject !== 'string' || !stored.account.subject
            || typeof stored.account?.tenantId !== 'string' || !stored.account.tenantId
            || (stored.account.email !== null && typeof stored.account.email !== 'string')
            || (stored.account.displayName !== null && typeof stored.account.displayName !== 'string'))
            return null;
        return this.contextFromEnvelope({
            sessionToken: stored.sessionToken, expiresAt: stored.expiresAt,
            account: { provider: 'microsoft', ...stored.account },
        }, null, true);
    }

    private contextMatchesStorage(context: OfficeSessionContext): boolean
    {
        if (!context.storageFence || this.storageEpoch() !== context.storageFence.epoch
            || this.storedSessionToken() !== context.sessionToken || this.office.isLoggedOutFlagSet()) return false;
        try
        {
            const preferred = this.office.getPreferredAccount();
            return !preferred || (preferred.tenantId === context.session.tenant_id
                && preferred.subject === context.session.user_id);
        }
        catch { return false; }
    }

    private storageEpoch(): string | null
    {
        return localStorage.getItem(STORAGE_EPOCH_KEY);
    }

    private advanceStorageEpoch(): string
    {
        const epoch = uuidv4();
        localStorage.setItem(STORAGE_EPOCH_KEY, epoch);
        return epoch;
    }

    private assertStorageFence(fence: OfficeStorageFence, context: OfficeSessionContext): void
    {
        if (this.storageEpoch() !== fence.epoch) throw new Error('Office session superseded in another pane');
        const currentToken = this.storedSessionToken();
        if (currentToken !== fence.sourceToken && currentToken !== context.sessionToken)
            throw new Error('Office session changed in another pane');
        if (fence.legacyPair)
        {
            const sessionId = localStorage.getItem('session_id');
            const deviceHash = localStorage.getItem('device_hash');
            const originalPair = sessionId === fence.legacyPair.sessionId
                && deviceHash === fence.legacyPair.deviceHash;
            const alreadyRetired = currentToken === context.sessionToken && !sessionId && !deviceHash;
            if (!originalPair && !alreadyRetired)
                throw new Error('Legacy Office session changed in another pane');
        }
        if (fence.recovery)
        {
            if (this.office.isLoggedOutFlagSet()) throw new Error('Office session signed out in another pane');
            const preferred = this.office.getPreferredAccount();
            if (preferred && (preferred.tenantId !== context.session.tenant_id
                || preferred.subject !== context.session.user_id))
                throw new Error('Office account changed in another pane');
        }
    }

    private publishContext(context: OfficeSessionContext | null): void
    {
        if (context)
        {
            if (!context.storageFence) throw new Error('Office session storage fence is missing');
            this.assertStorageFence(context.storageFence, context);
        }
        clearTimeout(this.refreshTimer);
        if (context?.expiresAt)
            this.refreshTimer = setTimeout(() => void this.coordinator.recover(), Math.max(5_000, context.expiresAt - Date.now() - 30_000));
        if (!context)
        {
            this.accessToken = null;
            this.tokenClaims = null;
            // Keep SessionBase's in-progress bootstrap object until initialization
            // finishes; it may still be filling the anonymous ID.
            if (this.sessionInfo?.user_id)
                this.sessionInfo = { session_id: this.sessionInfo.session_id,
                    ...(this.sessionInfo.device_hash ? { device_hash: this.sessionInfo.device_hash } : {}) };
            return;
        }
        const generation = this.coordinator.generation;
        this.sessionInfo = { ...context.session };
        this.accessToken = context.sessionToken;
        this.tokenClaims = { exp: Math.floor(context.expiresAt! / 1000) };
        this._sessionInfo$.next(this.sessionInfo);
        if (generation !== this.coordinator.generation) return;
        if (context.preferredAccount)
            this.office.setPreferredAccount(context.preferredAccount);
        this.loginHint = context.session.user_hint;
        if (generation !== this.coordinator.generation) return;
        if (context.clearLogoutFlag)
        {
            this.office.setLoggedOutFlag(false);
            this.needsAccountPicker = false;
        }
        if (generation !== this.coordinator.generation) return;
        this.authState$.next(true);
        if (generation !== this.coordinator.generation) return;
        void this.subscription.fetchSubscription(context.session.session_id)
            .catch(error => logger.error('Subscription hydration failed: ', error));
    }

    private requireCurrent(attempt: SessionAttempt): void
    {
        if (!attempt.isCurrent()) throw new Error('Office session operation superseded');
    }

    public logout(): Promise<void>
    {
        if (this.logoutWork) return this.logoutWork;
        const sourceToken = this.storedSessionToken();
        this.intentEpoch = this.advanceStorageEpoch();
        this.storageClearFence = { epoch: this.intentEpoch, sourceToken, recovery: false };
        this.needsAccountPicker = true;
        localStorage.removeItem(CHAT_COMPOSER_DRAFT_STORAGE_KEY);
        localStorage.removeItem(GROUP_CHAT_COMPOSER_DRAFT_STORAGE_KEY);
        this.rotatePersistedIds = true;
        // Set suppression before any network work; even a failing host logout
        // must leave the next explicit login on the existing account-picker path.
        try { this.office.setLoggedOutFlag(true); }
        catch (error) { logger.error('Office logout flag could not be saved: ', error); }
        const work = this.coordinator.logout().then(outcome =>
        {
            for (const failure of outcome.failures) logger.error('Office logout failed: ', failure.error);
        });
        this.logoutWork = work;
        void work.finally(() =>
        {
            if (this.logoutWork === work) this.logoutWork = null;
        });
        return work;
    }

    private async logoutOffice(sessionToken: string | null): Promise<void>
    {
        if (sessionToken) try
        {
            await firstValueFrom(this.http.post<null>(
                SESSION_ENDPOINTS.LOGOUT, {}, { headers: { Authorization: `Bearer ${ sessionToken }` } },
            ));
        }
            catch (error) { logger.error(error); }
        await this.office.logoutMsal();
    }
}
